import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, Plus, FileText, Upload, Printer, Trash2, DollarSign,
    Ship, MapPin, Package, Calendar, User, Filter, ChevronLeft,
    ChevronRight, ChevronsLeft, ChevronsRight, Eye, MoreHorizontal,
    Clock, CheckCircle2, AlertCircle, ArrowUpDown, ArrowDownZA, ArrowUpAZ, Mail,
    TrendingUp,
    Sparkles,
    ArrowUpRight,
    ArrowDownRight,
    Globe,
    XCircle,
    Brain,
    RotateCcw,
    Import
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const AIInsightsModal = ({
    onClose = () => { },
    insightCards = [],
}) => {

    const statThemes = [
        {
            bg: "bg-blue-50 dark:bg-blue-500/10",
            text: "text-blue-600 dark:text-blue-400",
            subText: "text-blue-600/70 dark:text-blue-400/70",
        },
        {
            bg: "bg-emerald-50 dark:bg-emerald-500/10",
            text: "text-emerald-600 dark:text-emerald-400",
            subText: "text-emerald-600/70 dark:text-emerald-400/70",
        },
        {
            bg: "bg-red-50 dark:bg-red-500/10",
            text: "text-red-600 dark:text-red-400",
            subText: "text-red-600/70 dark:text-red-400/70",
        },
        {
            bg: "bg-purple-50 dark:bg-purple-500/10",
            text: "text-purple-600 dark:text-purple-400",
            subText: "text-purple-600/70 dark:text-purple-400/70",
        },
    ];

    const { t } = useTranslation();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                                <Brain className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-white">{t('aiQuoteAnalytics')}</h2>
                                <p className="text-purple-100 text-sm">{t('aiQuoteAnalyticsDescription')}</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                            <XCircle className="w-5 h-5 text-white" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[60vh]">
                    {/* Summary Stats */}
                    <div className="grid grid-cols-4 gap-4 mb-6">

                        {insightCards.map((card, index) => (
                            <div
                                key={card.title}
                                className={`${statThemes[index].bg} rounded-xl p-4 text-center`}
                            >
                                <p className={`text-2xl font-bold ${statThemes[index].text}`}>
                                    {card.value}
                                </p>
                                <p className={`text-xs font-medium ${statThemes[index].subText}`}>
                                    {card.title}
                                </p>
                            </div>
                        ))}

                    </div>

                    {/* AI Predictions */}
                    <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-500/10 dark:to-pink-500/10 rounded-xl p-5 mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            <h3 className="font-bold text-slate-800 dark:text-white">{t('aiPredictions')}</h3>
                        </div>
                        <div className="space-y-3">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{t('highConversionPotential')}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('highConversionPotentialDescription')}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <AlertCircle className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{t('expiringSoon')}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('expiringSoonDescription')}</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <TrendingUp className="w-3.5 h-3.5 text-white" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">{t('marketTrend')}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">{t('marketTrendDescription')}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Top Routes & Customers */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <Globe className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                                <h3 className="font-bold text-slate-800 dark:text-white">{t('topRoutes')}</h3>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { from: 'SAVANNAH, GA', to: 'DUBAI', count: 12 },
                                    { from: 'NEW YORK, NY', to: 'ROTTERDAM', count: 8 },
                                    { from: 'LOS ANGELES', to: 'SHANGHAI', count: 6 },
                                ].map((route, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-500/20 rounded-full flex items-center justify-center text-xs font-bold text-emerald-600 dark:text-emerald-400">{i + 1}</span>
                                        <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.from}</span>
                                        <span className="text-slate-400">→</span>
                                        <span className="text-xs text-slate-700 dark:text-slate-200 truncate">{route.to}</span>
                                        <span className="ml-auto text-xs font-bold text-slate-600 dark:text-slate-300">{route.count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-5">
                            <div className="flex items-center gap-2 mb-4">
                                <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
                                <h3 className="font-bold text-slate-800 dark:text-white">{t('topCustomers')}</h3>
                            </div>
                            <div className="space-y-3">
                                {[
                                    { name: 'CLEAVER BROOKS CO.', quotes: 8 },
                                    { name: 'FAISAL TRADING', quotes: 5 },
                                    { name: 'SAKHAMET LTD', quotes: 4 },
                                ].map((customer, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <span className="w-6 h-6 bg-blue-100 dark:bg-blue-500/20 rounded-full flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">{i + 1}</span>
                                            <span className="text-xs text-slate-700 dark:text-slate-200">{customer.name}</span>
                                        </div>
                                        <span className="text-xs font-bold text-slate-600 dark:text-slate-300">{customer.quotes}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recommendations */}
                    <div className="mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-5 text-white">
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="w-5 h-5" />
                            <h3 className="font-bold">{t('aiRecommendations')}</h3>
                        </div>
                        <ul className="space-y-2 text-sm text-blue-100">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-300">•</span>
                                {t('followUpRecommendation')}
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-300">•</span>
                                {t('volumeDiscountRecommendation')}
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-300">•</span>
                                {t('renewedQuotesRecommendation')}
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-slate-200 dark:border-slate-700 px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('lastUpdatedJustNow')}</p>
                        <button onClick={onClose} className="px-4 py-2 bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium rounded-lg hover:bg-slate-700 transition-colors">
                            {t('close')}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}

export default AIInsightsModal;